import { createTrackingConfig, getLabels } from '../../components/generic/labels';
import { getDateFormat, tableConfig, uiConfig } from '../../config/appConfig';
import ExternalAuditFindingsTable from './components/ExternalAuditFindingsTable';
import CustomExternalAuditSplitView from './CustomExternalAuditSplitView';

const externalAuditTrackingLabels = getLabels('External Audit');
const externalAuditTrackingBase = createTrackingConfig('External Audit');

export const externalAuditConfig = {
  entityName: 'ExternalAudit',
  entityTypeId: 22,

  api: {
    baseEndpoint: '/external-audit',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`,
    },
    lookupEndpoints: {
      departments: '/department',
      users: '/users',
      externalAuditStatuses: '/external-audit-status',
      externalAuditCriticality: '/external-audit-criticality',
      externalAuditRootCauseCategory: '/external-audit-root-cause-category',
      capaActionStatus: '/capa-action-status',
      customers: '/customer',
    },
    swrInvalidation: {
      mainList: '/external-audit',
    },
  },

  ui: {
    drawerWidth: '50%',
    defaultPageSize: tableConfig.defaultPageSize,
    pageSizeOptions: tableConfig.pageSizeOptions,
    defaultTableDensity: tableConfig.defaultDensity,
    requiredFieldStyle: uiConfig.requiredFieldStyle,
    splitView: true,
    searchInputMaxWidth: uiConfig.searchInputMaxWidth,
    showViewButton: true,
    showEditButton: true,
    showDeleteButton: true,
  },

  customSplitViewPanel: {
    enabled: true,
    component: CustomExternalAuditSplitView,
  },

  features: {
    fileAttachments: false,
    masterEntityTypeId: 22,
  },

  displayName: 'External Audit',
  description: 'CAPA plans from external inspections (e.g. Kedrion)',
  icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',

  fieldsToRemove: [
    'id',
    'status',
    'department',
    'depositor',
    'closedBy',
    'createdBy',
    'updatedBy',
    'createdById',
    'updatedById',
    'createdAt',
    'updatedAt',
    'findings',
  ],
  fieldsToKeep: ['findings'],

  fields: [
    { name: 'id', label: 'ID', type: 'hidden', table: false, filterable: false },
    {
      name: 'code',
      label: 'Code',
      type: 'text',
      required: true,
      placeholder: 'e.g. Kedrion_02OCT2025',
      table: { header: 'Code', width: '160px', sortable: true },
      filterable: true,
      validation: { required: true, maxLength: 100, message: 'Code is required' },
    },
    {
      name: 'auditee',
      label: 'Auditee',
      type: 'text',
      required: true,
      placeholder: 'e.g. Kedrion',
      table: { header: 'Auditee', width: '140px', sortable: true },
      filterable: true,
      validation: { required: true, maxLength: 200, message: 'Auditee is required' },
    },
    {
      name: 'auditDate',
      label: 'Audit Date',
      type: 'date',
      required: true,
      table: {
        header: 'Audit Date',
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table'),
      },
      filterable: true,
      filterType: 'date',
      validation: { required: true, message: 'Audit date is required' },
    },
    {
      name: 'departmentId',
      label: 'Department',
      type: 'select',
      required: false,
      lookupEndpoint: 'departments',
      options: [],
      table: { header: 'Department', width: '140px', sortable: true, displayField: 'department' },
      filterable: true,
      filterType: 'multiSelect',
    },
    {
      name: 'siteType',
      label: 'Site Type',
      type: 'select',
      required: false,
      options: [
        { value: 'PLANT', label: 'Plant' },
        { value: 'WHS_DISTRIBUTION', label: 'WHS Distribution' },
        { value: 'SELF_INSPECTION', label: 'Self Inspection' },
        { value: 'N_A', label: 'N/A' },
      ],
      table: { header: 'Site Type', width: '130px', sortable: true },
      filterable: true,
      filterType: 'multiSelect',
    },
    {
      name: 'depositorId',
      label: 'Depositor',
      type: 'select',
      required: false,
      lookupEndpoint: 'customers',
      options: [],
      table: { header: 'Depositor', width: '140px', sortable: true, displayField: 'depositor' },
      filterable: true,
      filterType: 'multiSelect',
    },
    {
      name: 'statusId',
      label: 'Status',
      type: 'select',
      required: false,
      lookupEndpoint: 'externalAuditStatuses',
      options: [],
      table: { header: 'Status', width: '120px', sortable: true, displayField: 'status', cellRenderer: 'statusBadge' },
      filterable: true,
      filterType: 'multiSelect',
    },
    {
      name: 'closedAt',
      label: 'Closed At',
      type: 'date',
      required: false,
      table: {
        header: 'Closed At',
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table'),
      },
      filterable: true,
      filterType: 'date',
    },
    {
      name: 'findings',
      label: 'Findings & CAPA Actions',
      type: 'custom',
      required: false,
      table: false,
      filterable: false,
    },
  ],

  get tableColumns() {
    return this.fields
      .filter((f) => f.table !== false && f.table)
      .map((f) => ({
        key: f.table.displayField || f.name,
        header: f.table.header || f.label,
        width: f.table.width || 'auto',
        sortable: f.table.sortable !== false,
        displayField: f.table.displayField || f.name,
        cellRenderer: f.table.cellRenderer || 'default',
        originalField: f.name,
      }));
  },

  exportFileName: 'external_audits',

  fieldMappings: {
    status: 'status.name',
    department: 'department.name',
    depositor: 'depositor.name',
  },

  formSections: [
    {
      title: 'Audit header',
      icon: '📋',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['code', 'auditee', 'auditDate', 'departmentId', 'siteType', 'depositorId', 'statusId', 'closedAt'],
      fieldSpans: {
        code: 'md:col-span-3',
        auditee: 'md:col-span-3',
        auditDate: 'md:col-span-3',
        departmentId: 'md:col-span-3',
        siteType: 'md:col-span-3',
        depositorId: 'md:col-span-3',
        statusId: 'md:col-span-3',
        closedAt: 'md:col-span-3',
      },
    },
    {
      title: 'Findings & CAPA Actions',
      icon: '📄',
      layout: 'custom',
      fields: ['findings'],
      customComponent: ExternalAuditFindingsTable,
    },
  ],

  // Tracking — correct entity title; show users by display name (nested objects from API / row)
  trackingSection: {
    ...externalAuditTrackingBase,
    fields: externalAuditTrackingBase.fields.map((f) =>
      f.id === 'created'
        ? {
            ...f,
            userLabel: 'Created user:',
            dateLabel: externalAuditTrackingLabels.createdDateLabel,
            userEmptyText: externalAuditTrackingLabels.notSpecifiedText,
            dateEmptyText: externalAuditTrackingLabels.notSpecifiedText,
          }
        : f.id === 'updated'
          ? {
              ...f,
              userLabel: 'Updated user:',
              dateLabel: externalAuditTrackingLabels.updatedDateLabel,
              userEmptyText: externalAuditTrackingLabels.notModifiedText,
              dateEmptyText: externalAuditTrackingLabels.neverUpdatedText,
            }
          : f,
    ),
  },

  messages: {
    loading: 'Loading CAPA plans...',
    noData: 'No CAPA plans found.',
    createSuccess: 'CAPA plan created successfully!',
    updateSuccess: 'CAPA plan updated successfully!',
    deleteSuccess: 'CAPA plan deleted successfully!',
    createError: 'Failed to create CAPA plan',
    updateError: 'Failed to update CAPA plan',
    deleteError: 'Failed to delete CAPA plan',
    loadError: 'Failed to load CAPA plans',
    validationError: 'Validation failed',
  },

  transformData: (data) => {
    const transformed = { ...data };
    delete transformed.findings;
    delete transformed.createdById;
    delete transformed.updatedById;
    delete transformed.createdAt;
    delete transformed.updatedAt;
    const foreignKeyFields = ['departmentId', 'depositorId', 'statusId'];
    foreignKeyFields.forEach((field) => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) transformed[field] = null;
        else {
          const n = parseInt(transformed[field], 10);
          transformed[field] = !isNaN(n) ? n : null;
        }
      }
    });
    const dateFields = ['auditDate', 'closedAt'];
    dateFields.forEach((field) => {
      if (transformed[field] !== undefined && transformed[field] !== '' && transformed[field] !== null) {
        const d = new Date(transformed[field]);
        transformed[field] = !isNaN(d.getTime()) ? d : null;
      }
    });
    return transformed;
  },

  validationConfig: {
    enableRealTimeValidation: true,
    validateOnBlur: true,
    validateOnChange: false,
    showValidationSummary: true,
  },
};

export default externalAuditConfig;
